import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DisplayConfiguration } from '@carvana/showroom-tooltip/Base/Content';
import { useDualEventTracking } from '../../../../utils/useDualEventTracking';
import Modal from '@carvana/showroom-modal';
import {
  sendNativeMessage,
  NativeMessageEvent,
} from '@carvana/core-native-messaging';
import ErrorIcon from '../../../../assets/ErrorIcon';
import PaymentBreakdownDetails from './payment-breakdown-details';
import InfoToolTip from '../../../future-packages/info-tooltip';
import IneligibleAccountDetails from './ineligible-account-details';
import PayCurrentAmountPrompt from './pay-current-amount-prompt';
import { RootState } from '../../../../store/configureStore';
import {
  getPaymentEligiblity,
  getPaymentEligibilityByType,
} from '../../../../ducks/selectors';
import { PaymentEligibilityTypes } from '../../../../constants';
import {
  CurrentPaymentAutopayButton,
  CurrentPaymentTurnOffButton,
  CurrentPaymentButtonsWrapper,
  CurrentPaymentDetails,
  CurrentPaymentConnectBankWrapper,
  CurrentPaymentConnectGetStartedButton,
  CurrentPaymentDetailsWrapper,
  CurrentPaymentLabel,
  CurrentPaymentMakePaymentButton,
  CurrentPaymentTitle,
  CurrentPaymentValue,
  CurrentPaymentWrapper,
  PastDueStyle,
  CurrentPaymentDueDateLabel,
  ConnectBankValue,
} from './CurrentPayment.styles';
import { formatDate } from '../../../../utils/dateUtils';
import { formatCurrency } from '../../../../utils/CurrencyUtils';

const CurrentPayment = (props: { loanDetailCar: any }) => {
  const {
    loanDetailCar: {
      loanDetails,
      vehicle: { vehicleId },
      bankInfo,
    },
  } = props;

  const [ineligibleModalOpen, setIneligibleModalOpen] = useState(false); // All Payments Dialog
  const [oneTimeIneligibleModalOpen, setOneTimeIneligibleModalOpen] = useState(
    false
  );
  // One Time Payment Dialog
  const [autoPayIneligibleModalOpen, setAutoPayIneligibleModalOpen] = useState(
    false
  );
  // Auto Pay Dialog
  const [payCurrentAmountPromptOpen, setPayCurrentAmountPromptOpen] = useState(
    false
  );

  // Is Pay Current Amount Dialog to be displayed
  const isPayCurrentDueRequired = useSelector((state: RootState) =>
    getPaymentEligibilityByType(state, PaymentEligibilityTypes.PAY_CURRENT_DUE)
  );

  // Is One Time Payment Ineligible
  const isOneTimePaymentInEligible = useSelector((state: RootState) =>
    getPaymentEligibilityByType(state, PaymentEligibilityTypes.ONE_TIME_PAYMENT)
  );

  // Is Auto Payment Ineligible
  const isAutoPaymentInEligible = useSelector((state: RootState) =>
    getPaymentEligibilityByType(state, PaymentEligibilityTypes.AUTO_PAYMENT)
  );

  // Are all Payments Ineligible
  const isAllPaymentsInEligible = useSelector((state: RootState) =>
    getPaymentEligibilityByType(state, PaymentEligibilityTypes.ALL_PAYMENTS)
  );

  const paymentEligibility = useSelector(getPaymentEligiblity);

  const { trackEvent } = useDualEventTracking();
  const history = useHistory();

  const { isEnrolled: isEnrolledInAutoPay } =
    paymentEligibility?.recurringPaymentEligibility ?? {};

  const {
    cvnaPaymentAmountDue,
    principalDueDate,
    isPastDue,
    totalFees,
    lateFees,
    nsfFees: insufficientFundFees,
  } = loanDetails ?? {};

  const currentPaymentDue = cvnaPaymentAmountDue - totalFees;
  const payment = formatCurrency(cvnaPaymentAmountDue);
  const date = formatDate(principalDueDate);

  // One-time Payment
  const makePayment = () => {
    trackEvent('loan_details_one_time_pay_button_tap');

    // Show All Payments In-eligible
    if (isAllPaymentsInEligible) {
      setIneligibleModalOpen(true);
      return;
    }

    // Show One-time Payment In-eligible
    if (isOneTimePaymentInEligible) {
      setOneTimeIneligibleModalOpen(true);
      return;
    }

    history.push(`/loan-detail/make-payment/${vehicleId}`);
  };

  // Auto-pay
  const setUpAutopay = () => {
    trackEvent('loan_details_auto_pay_button_tap');
    // Display Pay Current Amount Due Dialog
    if (isPayCurrentDueRequired) {
      setPayCurrentAmountPromptOpen(true);
      return;
    }

    // Show All Payments In-eligible
    if (isAllPaymentsInEligible) {
      setIneligibleModalOpen(true);
      return;
    }

    // Show Auto Pay In-eligible
    if (isAutoPaymentInEligible) {
      trackEvent('auto_pay_ineligible_dialogue');
      setAutoPayIneligibleModalOpen(true);
      return;
    }

    sendNativeMessage('enable_auto_pay' as NativeMessageEvent, {}, 'mycars');

    history.push(`/loan-detail/set-up-autopay/${vehicleId}`);
  };

  const turnOffAutoPay = () => {
    history.push(`/loan-detail/turn-off-autopay/${vehicleId}`);
  };
  const connectGetStarted = () => {
    trackEvent('loan_details_connect_bank_button_tap');
    history.push(`/loan-detail/connect-bank/${vehicleId}`);
  };

  const ErrorIconStyles = {
    color: '#E03131', // red-2  https://showroom.cvnacorp.com/carvana-css-theme
    marginLeft: '8px',
  };

  const toolTipDisplay: Partial<DisplayConfiguration> = {
    height: 210,
    arrowOffsetX: -20,
    contentOffsetX: 30,
  };

  // TODO - convert this to function that returns a tag
  const AutoPayButton = !isEnrolledInAutoPay ? (
    <CurrentPaymentAutopayButton onClick={setUpAutopay}>
      Set Up Autopay
    </CurrentPaymentAutopayButton>
  ) : (
    <CurrentPaymentTurnOffButton onClick={turnOffAutoPay}>
      Turn off AutoPay
    </CurrentPaymentTurnOffButton>
  );

  const PaymentBreakdownTooltip = (
    <InfoToolTip
      display={toolTipDisplay}
      trackEventContent="amount_due_tooltip_tap"
      content={() => (
        <PaymentBreakdownDetails
          currentPaymentDue={currentPaymentDue}
          lateCharges={lateFees}
          insufficientFundFees={insufficientFundFees}
          total={cvnaPaymentAmountDue}
        />
      )}
    />
  );

  return (
    <>
      <CurrentPaymentWrapper>
        <CurrentPaymentTitle>
          Current Payment
          {isPastDue && <ErrorIcon style={ErrorIconStyles} />}
        </CurrentPaymentTitle>
        <CurrentPaymentDetailsWrapper>
          <CurrentPaymentDetails
            className={isEnrolledInAutoPay ? 'turn-off-pay' : 'setup-pay'}
          >
            <>
              {' '}
              {isPastDue ? (
                <PastDueStyle>
                  {payment}
                  {PaymentBreakdownTooltip}
                </PastDueStyle>
              ) : (
                <CurrentPaymentValue>
                  {payment}
                  {PaymentBreakdownTooltip}
                </CurrentPaymentValue>
              )}
            </>
            <CurrentPaymentLabel>Amount Due</CurrentPaymentLabel>
          </CurrentPaymentDetails>

          <CurrentPaymentDetails>
            <>
              {' '}
              {isPastDue ? (
                <PastDueStyle>Due Now</PastDueStyle>
              ) : (
                <CurrentPaymentValue>{date}</CurrentPaymentValue>
              )}
            </>
            <CurrentPaymentDueDateLabel>Due Date</CurrentPaymentDueDateLabel>
          </CurrentPaymentDetails>
        </CurrentPaymentDetailsWrapper>
        <>
          {bankInfo && bankInfo.length > 0 ? (
            <CurrentPaymentButtonsWrapper>
              <CurrentPaymentMakePaymentButton onClick={makePayment}>
                Make A Payment
              </CurrentPaymentMakePaymentButton>
              {AutoPayButton}
            </CurrentPaymentButtonsWrapper>
          ) : (
            <CurrentPaymentConnectBankWrapper>
              <ConnectBankValue>
                Link your bank to make payments to your Bridgecrest account.
              </ConnectBankValue>
              <CurrentPaymentConnectGetStartedButton
                onClick={connectGetStarted}
              >
                Link Bank Account
              </CurrentPaymentConnectGetStartedButton>
            </CurrentPaymentConnectBankWrapper>
          )}
        </>
      </CurrentPaymentWrapper>
      {/* All Payments ineligible dialog */}
      <Modal
        headerText="NOT ELIGIBLE"
        disableBackdropClick
        mode="light"
        isOpen={ineligibleModalOpen}
        closeModal={() => {
          setIneligibleModalOpen(false);
        }}
      >
        <IneligibleAccountDetails title="THIS ACCOUNT ISN’T ELIGIBLE TO MAKE A PAYMENT." />
      </Modal>
      {/* Auto pay ineligible dialog */}
      <Modal
        headerText="NOT ELIGIBLE"
        disableBackdropClick
        mode="light"
        isOpen={autoPayIneligibleModalOpen}
        closeModal={() => {
          trackEvent('auto_pay_ineligible_dialogue_exit_trap');
          setAutoPayIneligibleModalOpen(false);
        }}
      >
        <IneligibleAccountDetails title="THIS ACCOUNT ISN’T ELIGIBLE TO ENROLL IN AUTO-PAY." />
      </Modal>
      {/* One-time pay ineligible dialog */}
      <Modal
        headerText="NOT ELIGIBLE"
        disableBackdropClick
        mode="light"
        isOpen={oneTimeIneligibleModalOpen}
        closeModal={() => {
          setOneTimeIneligibleModalOpen(false);
        }}
      >
        <IneligibleAccountDetails title="THIS ACCOUNT ISN’T ELIGIBLE TO MAKE A ONE-TIME PAYMENT." />
      </Modal>
      {/* Pay Due Dialog */}
      <PayCurrentAmountPrompt
        vehicleId={vehicleId}
        openModal={payCurrentAmountPromptOpen}
        toggleModal={() =>
          setPayCurrentAmountPromptOpen(!payCurrentAmountPromptOpen)
        }
        promptCloseTrackEvent="auto_pay_requires_one_time_payment_dialogue_exit_tap"
        promptOpenTrackEvent="auto_pay_requires_one_time_payment_dialogue"
      />
    </>
  );
};

export default CurrentPayment;
